<template>
  <v-autocomplete
    density="compact"
    variant="outlined"
    hide-details="auto"
    :model-value="modelValue"
    @update:modelValue="updateValue"
    v-model:search="searchText"
    :items="items"
    :item-title="itemTitle"
    item-value="code"
    :filter-keys="filterKeys"
    :custom-filter="filterByKeywords"
    :label="label"
    :multiple="true"
    chips
    closable-chips
    clearable
    @keydown="removeLastByBackspace"
  >
    <!-- 検索語にヒットした候補をまとめて選択 -->
    <template #prepend-item>
      <template v-if="matchedItems.length > 1">
        <v-list-item @click="selectAllMatched">
          <v-list-item-title class="text-primary">
            {{ $t('common.label.selectAllMatched', { count: matchedItems.length }) }}
          </v-list-item-title>
        </v-list-item>
        <v-divider />
      </template>
    </template>
    <template #item="{ item, props }">
      <!-- #itemスロット使用時はVuetifyがkeyを付与しないため明示的に指定する。
           省略すると絞り込み変化時に行が位置(index)で再利用され、
           チェックボックスの表示状態が別のロケに残ってしまう -->
      <v-list-item v-bind="props" :key="item.value" :title="itemTitle(item.raw)">
        <template #prepend="{ isSelected }">
          <v-checkbox-btn :model-value="isSelected" :ripple="false" />
        </template>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script>
/**
 * ロケ(店舗)複数選択部品
 * - 選択済みロケはチップ表示し、検索用の入力欄は選択後に空にする
 * - 検索語が空の状態でBackspaceを押すと、最後に選択したロケを解除する
 * - 区切り文字(＊ / 全角・半角スペース / カンマ)で複数キーワードを入力した場合はOR検索
 * - 検索対象は表示内容に関わらず、ロケコード(plantコード＝m_sitezone.zone_code)と
 *   ロケ名(店舗名＝m_sitezone.zone_name)の両方(部分一致)
 * items: { code: ロケコード, name: ロケ名 } の配列
 */
const KEYWORD_SEPARATOR = /[*\s,、]+/;
// 文字検索の対象項目
const SEARCH_KEYS = ["code", "name"];

// 全角/半角カナ・英数字の差を吸収して比較する
function normalize(value) {
  return String(value ?? "").normalize("NFKC").toLowerCase();
}

function splitKeywords(query) {
  return normalize(query).split(KEYWORD_SEPARATOR).filter(e => e != "");
}

export default {
  props: {
    modelValue: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    label: String,
    // チップの表示内容 code：ロケコード / name：ロケ名 / codeName：両方
    displayMode: { type: String, default: "codeName" },
  },
  emits: ["update:modelValue"],
  data: () => ({
    searchText: "",
    filterKeys: SEARCH_KEYS.map(k => "raw." + k),
  }),
  computed: {
    // 現在の検索語にヒットする候補
    matchedItems() {
      const keywords = splitKeywords(this.searchText);
      if (keywords.length == 0) return [];
      return this.items.filter(item => this.matches(item, keywords));
    },
  },
  methods: {
    itemTitle(item) {
      // 選択値(ロケコードの文字列)で呼ばれた場合はundefinedを返し、Vuetify側で値をそのまま使わせる
      if (item == null || typeof item != "object") return undefined;
      if (this.displayMode == "code") return item.code;
      if (this.displayMode == "name") return item.name;
      return item.code + "_" + item.name;
    },
    matches(item, keywords) {
      const targets = SEARCH_KEYS.map(key => normalize(item[key]));
      return keywords.some(k => targets.some(t => t.includes(k)));
    },
    // v-autocompleteのフィルター(キー毎に呼ばれる)
    filterByKeywords(value, query) {
      const keywords = splitKeywords(query);
      if (keywords.length == 0) return 0;
      const target = normalize(value);
      return keywords.some(k => target.includes(k)) ? 0 : -1;
    },
    selectAllMatched() {
      const codes = [...this.modelValue];
      this.matchedItems.forEach(item => {
        if (!codes.includes(item.code)) codes.push(item.code);
      });
      this.updateValue(codes);
      this.searchText = "";
    },
    // 検索語が空の時のBackspaceで最後のチップを削除(IME変換中は除く)
    removeLastByBackspace(e) {
      if (e.key != "Backspace" || e.isComposing) return;
      if (this.searchText || this.modelValue.length == 0) return;
      this.updateValue(this.modelValue.slice(0, -1));
    },
    updateValue(value) {
      this.$emit("update:modelValue", value ?? []);
    },
  },
};
</script>
